import { useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { useInvalidateSkillsCatalogQueries } from '~/hooks/queries/skills/useInvalidateSkillsCatalogQueries';
import { useEventSubscription } from '~/hooks/useEventSubscription';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type EventSubscriptionArgs } from '~/types/sockets';

export const SKILLS_CREATED_EVENT_TYPE = 'Activity.Workspace.Skills.created';
export const SKILLS_UPDATED_EVENT_TYPE = 'Activity.Workspace.Skills.updated';
export const SKILLS_DELETED_EVENT_TYPE = 'Activity.Workspace.Skills.deleted';

export const useHandleWorkspaceSkillsUpdated = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { invalidateSkillsCatalogQueries } = useInvalidateSkillsCatalogQueries();

  const createdEvent: EventSubscriptionArgs<'Activity.Workspace.Skills.created'> = {
    eventType: SKILLS_CREATED_EVENT_TYPE,
  };
  const updatedEvent: EventSubscriptionArgs<'Activity.Workspace.Skills.updated'> = {
    eventType: SKILLS_UPDATED_EVENT_TYPE,
  };
  const deletedEvent: EventSubscriptionArgs<'Activity.Workspace.Skills.deleted'> = {
    eventType: SKILLS_DELETED_EVENT_TYPE,
  };

  const options = useMemo(
    () => ({
      onUpdate: async () => {
        try {
          await invalidateSkillsCatalogQueries();
        } catch (error) {
          reportErrorToBugsnag({
            error,
            context: 'Failed to invalidate skills catalog queries after a skills WSS event',
          });
        }
      },
    }),
    [invalidateSkillsCatalogQueries],
  );

  useEventSubscription({
    currentWorkspaceId: currentWorkspace?.id,
    event: createdEvent,
    options,
  });
  useEventSubscription({
    currentWorkspaceId: currentWorkspace?.id,
    event: updatedEvent,
    options,
  });
  useEventSubscription({
    currentWorkspaceId: currentWorkspace?.id,
    event: deletedEvent,
    options,
  });
};
